import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { StatusPanel } from "../components/StatusPanel";
import {
  approveCrmOutputCandidate,
  createManualOpportunity, isAdminFixtureMode, listAdminOpportunities, listApplications,
  listAudit, listCrmOutputCandidates, listServiceHealth, rejectCrmOutputCandidate, reviewApplication, recordInvoice, createAndPayPayout, type ApplicationDecision, type ReviewScores,
} from "../lib/adminService";

export function AdminPage() {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState("");
  const [crmRejectNotes, setCrmRejectNotes] = useState<Record<string, string>>({});
  const [invoice, setInvoice] = useState({ opportunityId: "", invoiceReference: "", invoiceAmount: 0, currency: "EUR", connectorId: "preview-member" });
  const [payoutReference, setPayoutReference] = useState("");
  const [opportunity, setOpportunity] = useState({
    brand: "", market: "", warmSignal: "", aiScore: 80, expectedInvoice: 5000,
    currency: "EUR", publicSummary: "", minimumRole: "connector" as "connector" | "deal_operator",
  });
  const applications = useQuery({
    queryKey: ["admin-applications"],
    queryFn: listApplications,
  });
  const opportunities = useQuery({ queryKey: ["admin-opportunities"], queryFn: listAdminOpportunities });
  const health = useQuery({ queryKey: ["service-health"], queryFn: listServiceHealth });
  const crmCandidates = useQuery({ queryKey: ["crm-output-candidates"], queryFn: listCrmOutputCandidates });
  const audit = useQuery({ queryKey: ["audit"], queryFn: listAudit });
  const decision = useMutation({
    mutationFn: async ({ id, next, scores, notes }: { id: string; next: ApplicationDecision; scores: ReviewScores; notes: string }) => {
      await reviewApplication(id, next, scores, notes);
    },
    onSuccess: async () => {
      setNotice("Decision saved.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-applications"] }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
    },
  });
  const createOpportunity = useMutation({
    mutationFn: () => createManualOpportunity(opportunity),
    onSuccess: async () => {
      setNotice("Opportunity added to the queue.");
      setOpportunity((current) => ({ ...current, brand: "", warmSignal: "", publicSummary: "" }));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-opportunities"] }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
    },
  });
  const invoiceMutation = useMutation({
    mutationFn: async () => {
      const selected = opportunities.data?.find((item) => item.id === invoice.opportunityId);
      if (!selected) throw new Error("Choose an opportunity.");
      await recordInvoice({ ...invoice, twentyId: selected.twentyId });
    },
    onSuccess: async () => {
      setNotice("Invoice cleared and earnings placed on hold.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-opportunities"] }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
    },
  });
  const payoutMutation = useMutation({
    mutationFn: () => createAndPayPayout(payoutReference),
    onSuccess: async (count) => {
      setNotice(`${count} earnings marked paid.`);
      setPayoutReference("");
      await queryClient.invalidateQueries({ queryKey: ["audit"] });
    },
  });
  const approveCrm = useMutation({
    mutationFn: approveCrmOutputCandidate,
    onSuccess: async () => {
      setNotice("CRM output approved for Twenty writeback.");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["crm-output-candidates"] }),
        queryClient.invalidateQueries({ queryKey: ["audit"] }),
      ]);
    },
  });
  const rejectCrm = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) => rejectCrmOutputCandidate(id, notes),
    onSuccess: async () => {
      setNotice("CRM output rejected.");
      await queryClient.invalidateQueries({ queryKey: ["crm-output-candidates"] });
    },
  });

  return (
    <div className="admin-review-shell">
      <header className="admin-review-topbar">
        <div><h1>Pilot operations</h1></div>
        <div className="hero-actions"><span className="tag">{isAdminFixtureMode ? "FIXTURE MODE" : "LIVE"}</span><Link className="btn" to="/admin/qa">QA CONSOLE</Link><Link className="btn" to="/admin/workflows">WORKFLOWS</Link><Link className="btn" to="/admin/cells">CELLS</Link><Link className="btn" to="/admin/payouts">PAYOUTS</Link><Link className="btn" to="/app">MEMBER APP</Link></div>
      </header>
      <main className="admin-review-main full">
        {notice && <StatusPanel title="Saved" message={notice} kind="success" />}
        <div className="stat-grid">
          <div className="stat"><span>APPLICATIONS</span><strong>{applications.data?.length ?? 0}</strong><small>IN QUEUE</small></div>
          <div className="stat"><span>ACTIVE MEMBERS</span><strong>1 / 5</strong><small>PILOT CAP</small></div>
          <div className="stat"><span>OPPORTUNITIES</span><strong>{opportunities.data?.filter((item) => item.status === "open").length ?? 0}</strong><small>OPEN NOW</small></div>
          <div className="stat"><span>SERVICES READY</span><strong>{health.data?.filter((item) => item.status === "ok").length ?? 0}</strong><small>OF {health.data?.length ?? 0}</small></div>
        </div>
        <section className="admin-review-panel">
          {applications.isError && <StatusPanel title="Admin access required" message="Your account must have the admin or reviewer role." kind="error" />}
          {decision.isError && <StatusPanel title="Decision failed" message={decision.error.message} kind="error" />}
          <div className="section-head"><div><h2>Applicants</h2></div><p>HUMAN REVIEW · 5 × 20</p></div>
          <div className="admin-table">
            {(applications.data ?? []).map((item) => (
              <article className="applicant-row" key={item.id}>
                <div>
                  <strong>{item.name}</strong>
                  <small>{item.email} · {item.discordUsername}</small>
                  <p>{item.whyEntry}</p>
                  <p><strong>{item.primaryRoute?.toUpperCase()}</strong> / backup {item.backupRoute} · {item.availabilityBand} hours</p>
                  <ApplicationSignals item={item} />
                  <details><summary>Versioned work sample</summary><small>{item.sampleVersion}</small><p>{item.sampleResponse}</p><small>Tools: {item.tools || "None listed"} · Link: {item.portfolioUrl || "None"}</small></details>
                </div>
                <div><span>{item.country}</span><small>{new Date(item.submittedAt).toLocaleDateString()} · {item.guardianApproved ? "GUARDIAN APPROVED" : "NO GUARDIAN APPROVAL"}</small></div>
                <strong>{item.status.replace("_", " ")}</strong>
                <ReviewControls disabled={item.status === "guardian_pending"} onDecision={(next, scores, notes) => decision.mutate({ id: item.id, next, scores, notes })} />
              </article>
            ))}
          </div>
        </section>

        <section className="admin-review-panel">
          <div className="section-head"><div><h2>Legacy invoice ledger</h2></div><p>NEW MISSIONS USE 70 / 25 / 3 / 2</p></div>
          {(invoiceMutation.isError || payoutMutation.isError) && <StatusPanel title="Finance action failed" message={(invoiceMutation.error ?? payoutMutation.error)?.message ?? "Try again."} kind="error" />}
          <div className="form-grid">
            <label><span>Opportunity</span><select className="field" value={invoice.opportunityId} onChange={(event) => {
              const selected = opportunities.data?.find((item) => item.id === event.target.value);
              setInvoice({ ...invoice, opportunityId: event.target.value, invoiceAmount: selected?.expectedInvoice ?? 0, currency: selected?.currency ?? "EUR" });
            }}><option value="">Choose one</option>{(opportunities.data ?? []).filter((item) => item.status !== "closed").map((item) => <option value={item.id} key={item.id}>{item.brand}</option>)}</select></label>
            <Field label="Invoice reference" value={invoice.invoiceReference} onChange={(value) => setInvoice({ ...invoice, invoiceReference: value })} />
            <Field label="Invoice amount" type="number" value={String(invoice.invoiceAmount)} onChange={(value) => setInvoice({ ...invoice, invoiceAmount: Number(value) })} />
            <Field label="Connector member ID" value={invoice.connectorId} onChange={(value) => setInvoice({ ...invoice, connectorId: value })} />
          </div>
          <button className="btn primary" disabled={!invoice.opportunityId || invoice.invoiceReference.length < 3 || invoiceMutation.isPending} onClick={() => invoiceMutation.mutate()}>RECORD CLEARED INVOICE</button>
          <div className="section-head"><div><h3>Mark approved earnings paid</h3></div></div>
          <div className="form-grid"><Field label="External payment reference" value={payoutReference} onChange={setPayoutReference} /></div>
          <button className="btn" disabled={payoutReference.length < 3 || payoutMutation.isPending} onClick={() => payoutMutation.mutate()}>CREATE AND MARK PAYOUT PAID</button>
        </section>

        <section className="admin-review-panel">
          <div className="section-head"><div><h2>CRM output approvals</h2></div><p>APPROVED WORKER OUTPUT · ADMIN WRITEBACK GATE</p></div>
          {(approveCrm.isError || rejectCrm.isError || crmCandidates.isError) && <StatusPanel title="CRM approval failed" message={(approveCrm.error ?? rejectCrm.error ?? crmCandidates.error)?.message ?? "Try again."} kind="error" />}
          <div className="data-list">
            {(crmCandidates.data ?? []).map((candidate) => (
              <div className="data-row crm-output-row" key={candidate.id}>
                <div className="data-main">
                  <strong>{candidate.targetName}</strong>
                  <small>{candidate.productName} · {candidate.route.toUpperCase()} · {candidate.candidateType.replace("_", " ")} → {candidate.twentyObject}</small>
                  <p>{String(candidate.workerProof.icpReason ?? candidate.workerProof.context ?? "Approved proof is ready for CRM decision.")}</p>
                  {candidate.targetUrl && <a href={candidate.targetUrl} target="_blank" rel="noreferrer">Open source</a>}
                </div>
                <div className="data-cell"><span className="tag">{candidate.adminCrmDecision}</span><small>{candidate.writebackStatus}</small></div>
                <div className="crm-output-actions">
                  <button className="btn primary" disabled={candidate.adminCrmDecision !== "pending" || approveCrm.isPending} onClick={() => approveCrm.mutate(candidate.id)}>APPROVE CRM</button>
                  <input className="field" placeholder="Reject reason" value={crmRejectNotes[candidate.id] ?? ""} onChange={(event) => setCrmRejectNotes({ ...crmRejectNotes, [candidate.id]: event.target.value })} />
                  <button className="btn" disabled={candidate.adminCrmDecision !== "pending" || (crmRejectNotes[candidate.id] ?? "").trim().length < 5 || rejectCrm.isPending} onClick={() => rejectCrm.mutate({ id: candidate.id, notes: crmRejectNotes[candidate.id] ?? "" })}>REJECT</button>
                </div>
              </div>
            ))}
            {!crmCandidates.isLoading && !crmCandidates.data?.length && <div className="member-empty"><span>◇</span><h3>No CRM outputs waiting.</h3><p>Approved worker target results will appear here before Twenty writeback.</p></div>}
          </div>
        </section>

        <section className="admin-review-panel">
          <div className="section-head"><div><h2>Add opportunity</h2></div><p>TWENTY REMAINS PRIMARY</p></div>
          {createOpportunity.isError && <StatusPanel title="Could not add opportunity" message={createOpportunity.error.message} kind="error" />}
          <div className="form-grid">
            <Field label="Brand" value={opportunity.brand} onChange={(value) => setOpportunity({ ...opportunity, brand: value })} />
            <Field label="Market" value={opportunity.market} onChange={(value) => setOpportunity({ ...opportunity, market: value })} />
            <Field label="Warm signal" value={opportunity.warmSignal} onChange={(value) => setOpportunity({ ...opportunity, warmSignal: value })} />
            <Field label="AI score" type="number" value={String(opportunity.aiScore)} onChange={(value) => setOpportunity({ ...opportunity, aiScore: Number(value) })} />
            <Field label="Expected invoice" type="number" value={String(opportunity.expectedInvoice)} onChange={(value) => setOpportunity({ ...opportunity, expectedInvoice: Number(value) })} />
            <Field label="Public summary" value={opportunity.publicSummary} onChange={(value) => setOpportunity({ ...opportunity, publicSummary: value })} />
          </div>
          <button className="btn primary" disabled={createOpportunity.isPending || opportunity.brand.length < 2 || opportunity.publicSummary.length < 10} onClick={() => createOpportunity.mutate()}>ADD TO QUEUE</button>
        </section>

        <section className="admin-review-panel">
          <div className="section-head"><div><h2>Service health</h2></div><p>NO KEYS REQUIRED YET</p></div>
          <div className="data-list">
            {(health.data ?? []).map((item) => (
              <div className="data-row" key={item.service}>
                <div className="data-main"><strong>{item.service}</strong><small>{item.detail}</small></div>
                <div className="data-cell"><span className="tag">{item.status.replace("_", " ")}</span></div>
              </div>
            ))}
          </div>
        </section>

        <section className="admin-review-panel">
          <div className="section-head"><div><h2>Audit log</h2></div><p>LAST 50 EVENTS</p></div>
          <div className="data-list">
            {(audit.data ?? []).map((event) => (
              <div className="data-row" key={event.id}>
                <div className="data-main"><strong>{event.eventType}</strong><small>{event.entity}</small></div>
                <div className="data-cell"><span>{new Date(event.createdAt).toLocaleString()}</span></div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function ApplicationSignals({ item }: { item: import("../lib/adminService").AdminApplication }) {
  const signals = parseQualificationSignals(item.qualificationSignals);
  if (!signals) return null;
  return (
    <details>
      <summary>Platform fit signals</summary>
      <div className="application-signal-grid">
        <small>Status: {String(signals.currentStatus ?? "Not provided")}</small>
        <small>Work style: {String(signals.workPreference ?? "Not provided")}</small>
        <small>Used SOP/checklist: {String(signals.dealExperience ?? "No")}</small>
        <small>Understands approved-proof pay: {String(signals.commissionExperience ?? "No")}</small>
        <small>Understands task money: {String(signals.canSurviveNoFixedIncome ?? "No")}</small>
        <small>City: {String(signals.cityCountry ?? "Not provided")}</small>
        <small>Instagram: {String(signals.instagramHandle ?? "Not provided")}</small>
        <small>WhatsApp: {String(signals.whatsapp ?? "Not provided")}</small>
        <small>Video: {String(signals.videoLink ?? "None")}</small>
      </div>
      <p><strong>Best simple tasks:</strong> {String(signals.obsession ?? "Not provided")}</p>
      <p><strong>Careful task example:</strong> {String(signals.outworkedMoment ?? "Not provided")}</p>
      <p><strong>If proof is rejected:</strong> {String(signals.rejectionResponse ?? "Not provided")}</p>
      <p><strong>What makes tasks hard:</strong> {String(signals.quitReason ?? "Not provided")}</p>
      <p><strong>Rules and no spam:</strong> {String(signals.loyaltyUnderPressure ?? "Not provided")}</p>
      <p><strong>First task pace:</strong> {String(signals.firstThirtyDaysPlan ?? "Not provided")}</p>
    </details>
  );
}

function parseQualificationSignals(value: string) {
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function ReviewControls({ onDecision, disabled = false }: { onDecision: (decision: ApplicationDecision, scores: ReviewScores, notes: string) => void; disabled?: boolean }) {
  const [scores, setScores] = useState<ReviewScores>({ routeFit: 0, judgment: 0, communication: 0, execution: 0, integrity: 0 });
  const [notes, setNotes] = useState("");
  const total = Object.values(scores).reduce((sum, value) => sum + value, 0);
  function update(key: keyof ReviewScores, value: string) {
    const caps: ReviewScores = { routeFit: 20, judgment: 20, communication: 20, execution: 20, integrity: 20 };
    setScores((current) => ({ ...current, [key]: Math.max(0, Math.min(caps[key], Number(value))) }));
  }
  return (
    <div className="review-controls">
      <div className="score-row">
        <label>Route fit<input className="field" type="number" value={scores.routeFit} onChange={(event) => update("routeFit", event.target.value)} /></label>
        <label>Judgment<input className="field" type="number" value={scores.judgment} onChange={(event) => update("judgment", event.target.value)} /></label>
        <label>Communication<input className="field" type="number" value={scores.communication} onChange={(event) => update("communication", event.target.value)} /></label>
        <label>Execution<input className="field" type="number" value={scores.execution} onChange={(event) => update("execution", event.target.value)} /></label>
        <label>Integrity<input className="field" type="number" value={scores.integrity} onChange={(event) => update("integrity", event.target.value)} /></label>
      </div>
      <label>Reviewer notes<textarea className="field" value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
      <span className="score-total">TOTAL {total}/100 · REVIEWER DECIDES</span>
      <div className="row-actions">
        <button className="btn primary" disabled={disabled || total === 0 || notes.trim().length < 10} onClick={() => onDecision("invited", scores, notes)}>INVITE</button>
        <button className="btn" disabled={disabled || total === 0 || notes.trim().length < 10} onClick={() => onDecision("waitlisted", scores, notes)}>WAITLIST</button>
        <button className="btn" disabled={disabled || total === 0 || notes.trim().length < 10} onClick={() => onDecision("declined", scores, notes)}>DECLINE</button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text" }: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return <label><span>{label}</span><input className="field" type={type} value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}
